import { isDemoReadOnlyUser } from "./demoUser";

const STORAGE_PREFIX = "gps:demo-local:";

const ENTITY_CONFIG = {
  trips: {
    createPath: "/logistics/trips",
    listPath: "/logistics/trips",
    defaultValues: { status: "draft", tripStatus: "draft" },
  },
  expenses: {
    createPath: "/logistics/expenses",
    listPath: "/logistics/expenses",
    defaultValues: { status: "active" },
  },
  materials: {
    createPath: "/logistics/materials",
    listPath: "/logistics/materials",
    defaultValues: { status: "active" },
  },
  documents: {
    createPath: "/logistics/documents",
    listPath: "/logistics/documents",
    defaultValues: { status: "active" },
  },
  routes: {
    createPath: "/logistics/routes",
    listPath: "/logistics/routes",
    defaultValues: { status: "active" },
  },
  sensors: {
    createPath: "/sensors",
    listPath: "/sensors",
    responseShape: "data-array",
    defaultValues: { status: "active" },
  },
  reminders: {
    createPath: "/reminders",
    listPath: "/reminders",
    responseShape: "data-array",
    defaultValues: { status: "active" },
  },
  clients: {
    createPath: "/clients",
    listPath: "/clients",
    defaultValues: { status: "active" },
  },
  groups: {
    createPath: "/groups",
    listPath: "/groups",
    responseShape: "paginated",
    defaultValues: { status: "active" },
  },
};

const normalizePath = (value) => `/${String(value || "").replace(/^\/+/, "")}`;

const safeParse = (raw, fallback) => {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch (_) {
    return fallback;
  }
};

const toPlainObject = (body) => {
  if (!body) return {};
  if (body instanceof FormData) {
    const out = {};
    for (const [key, value] of body.entries()) {
      if (value instanceof File) {
        out[key] = value.name;
      } else {
        out[key] = value;
      }
    }
    return out;
  }
  if (typeof body === "object") return { ...body };
  return { value: body };
};

const storageKeyFor = (entity) => `${STORAGE_PREFIX}${entity}`;

const readDemoLocalItems = (entity) =>
  safeParse(localStorage.getItem(storageKeyFor(entity)), []);

const writeDemoLocalItems = (entity, items) => {
  localStorage.setItem(storageKeyFor(entity), JSON.stringify(items));
};

const nowIso = () => new Date().toISOString();

const createDemoLocalItem = (entity, body) => {
  const config = ENTITY_CONFIG[entity] || {};
  const payload = toPlainObject(body);
  const createdAt = nowIso();
  return {
    _id: `demo-local-${entity}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    id: `demo-local-${entity}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    createdAt,
    updatedAt: createdAt,
    isDemoLocalOnly: true,
    ...config.defaultValues,
    ...payload,
  };
};

const prependUnique = (existing, item) => {
  const next = Array.isArray(existing) ? existing.filter((entry) => String(entry?._id || entry?.id) !== String(item?._id || item?.id)) : [];
  return [item, ...next];
};

const mergeArrayItems = (serverItems, localItems) => {
  const base = Array.isArray(serverItems) ? [...serverItems] : [];
  const seen = new Set(base.map((item) => String(item?._id || item?.id || "")));
  const locals = Array.isArray(localItems) ? localItems : [];
  locals.forEach((item) => {
    const id = String(item?._id || item?.id || "");
    if (!seen.has(id)) {
      base.unshift(item);
      seen.add(id);
    }
  });
  return base;
};

export const getDemoLocalEntityFromRequest = ({ url, method }) => {
  const normalizedMethod = String(method || "GET").toUpperCase();
  const normalizedUrl = normalizePath(url);
  if (normalizedMethod === "DELETE") {
    return Object.entries(ENTITY_CONFIG).find(([, config]) => {
      const targetPath = normalizePath(config.listPath);
      return normalizedUrl.startsWith(`${targetPath}/`);
    })?.[0] || null;
  }
  return Object.entries(ENTITY_CONFIG).find(([, config]) => {
    const targetPath = normalizedMethod === "POST" ? config.createPath : config.listPath;
    return normalizePath(targetPath) === normalizedUrl;
  })?.[0] || null;
};

export const isDemoLocalCrudRequest = ({ user, url, method }) =>
  Boolean(isDemoReadOnlyUser(user) && getDemoLocalEntityFromRequest({ url, method }));

export const handleDemoLocalCreate = ({ user, url, method, body }) => {
  if (!isDemoReadOnlyUser(user) || String(method || "").toUpperCase() !== "POST") return null;
  const entity = getDemoLocalEntityFromRequest({ url, method });
  if (!entity) return null;

  const nextItem = createDemoLocalItem(entity, body);
  const stored = readDemoLocalItems(entity);
  writeDemoLocalItems(entity, prependUnique(stored, nextItem));

  const shape = ENTITY_CONFIG[entity]?.responseShape;
  if (shape === "data-array") {
    return { data: { success: true, data: nextItem, isDemoLocalOnly: true } };
  }
  if (shape === "paginated") {
    return { data: { data: nextItem, isDemoLocalOnly: true } };
  }
  return { data: nextItem };
};

export const handleDemoLocalDelete = ({ user, url, method }) => {
  if (!isDemoReadOnlyUser(user) || String(method || "").toUpperCase() !== "DELETE") return null;
  const entity = getDemoLocalEntityFromRequest({ url, method });
  if (!entity) return null;

  const normalizedUrl = normalizePath(url);
  const entityPath = normalizePath(ENTITY_CONFIG[entity]?.listPath);
  const targetId = normalizedUrl.startsWith(`${entityPath}/`)
    ? normalizedUrl.slice(entityPath.length + 1)
    : "";

  if (!targetId || !isDemoLocalId(targetId, entity)) return null;

  const existingItems = readDemoLocalItems(entity);
  const nextItems = existingItems.filter(
    (item) => String(item?._id || item?.id || "") !== String(targetId)
  );
  writeDemoLocalItems(entity, nextItems);

  return {
    data: {
      success: true,
      message: "Demo local item deleted successfully.",
      id: targetId,
      isDemoLocalOnly: true,
    },
  };
};

export const isDemoLocalId = (value, entity = "") => {
  const id = String(value || "");
  const entityPart = String(entity || "").trim();
  return entityPart
    ? id.startsWith(`demo-local-${entityPart}-`)
    : id.startsWith("demo-local-");
};

export const readDemoLocalItem = (entity, id) => {
  const targetId = String(id || "");
  if (!targetId) return null;
  return (
    readDemoLocalItems(entity).find(
      (item) => String(item?._id || item?.id || "") === targetId
    ) || null
  );
};

export const mergeDemoLocalListResponse = ({ user, url, method, responseData }) => {
  if (!isDemoReadOnlyUser(user) || String(method || "GET").toUpperCase() !== "GET") {
    return responseData;
  }

  const entity = getDemoLocalEntityFromRequest({ url, method: "GET" });
  if (!entity) return responseData;

  const localItems = readDemoLocalItems(entity);
  const shape = ENTITY_CONFIG[entity]?.responseShape;

  if (shape === "data-array") {
    const serverItems = Array.isArray(responseData?.data) ? responseData.data : [];
    return {
      ...(responseData && typeof responseData === "object" ? responseData : {}),
      data: mergeArrayItems(serverItems, localItems),
    };
  }

  if (shape === "paginated") {
    const serverItems = Array.isArray(responseData?.data) ? responseData.data : [];
    const merged = mergeArrayItems(serverItems, localItems);
    return {
      ...(responseData && typeof responseData === "object" ? responseData : {}),
      data: merged,
      pagination: {
        ...(responseData?.pagination || {}),
        totalCount: merged.length,
        totalPages: Math.max(1, Number(responseData?.pagination?.totalPages || 1)),
      },
    };
  }

  if (Array.isArray(responseData)) {
    return mergeArrayItems(responseData, localItems);
  }

  if (Array.isArray(responseData?.data)) {
    return {
      ...responseData,
      data: mergeArrayItems(responseData.data, localItems),
    };
  }

  return responseData;
};
